export interface PlantStage {
  id: string;
  name: string;
  duration: number; // in game-minutes
  description: string;
}

export interface PlantStateConfig {
  posture: string;
  colorSaturation: number;
  swayAmplitude: number;
  droopAngle: number;
  browning: number;
  alpha: number;
}

export interface EcologyConfig {
  light: { optimal: number; range: [number, number] };
  moisture: { optimal: number; range: [number, number] };
  temperature: { optimal: number; range: [number, number] };
}

export interface PlantSpecies {
  id: string;
  name: string;
  description: string;
  speciesBaseValue: number;
  bloomingIncomeRate: number; // 盛花阶段每游戏分钟每%健康度的收益
  stages: PlantStage[];
  states: Record<string, PlantStateConfig>;
  ecology: EcologyConfig;
}

export const Lavender: PlantSpecies = {
  id: 'lavender',
  name: '普罗旺斯薰衣草',
  description: '灰绿色的细叶，紫色的芬芳记忆',
  speciesBaseValue: 10,
  bloomingIncomeRate: 0.02,
  stages: [
    {
      id: 'seed',
      name: '种子',
      duration: 300, // 5 game hours
      description: '沉睡在土壤深处的小生命'
    },
    {
      id: 'sprout',
      name: '萌芽',
      duration: 480, // 8 game hours
      description: '两片肥厚的子叶顶开土壤'
    },
    {
      id: 'seedling',
      name: '幼苗',
      duration: 720, // 12 game hours
      description: '长出灰绿色细长真叶'
    },
    {
      id: 'budding',
      name: '抽穗',
      duration: 900, // 15 game hours
      description: '茎干拔高，出现紫色小花苞'
    },
    {
      id: 'blooming',
      name: '盛花',
      duration: Infinity,
      description: '紫色穗状花序绽放'
    }
  ],
  states: {
    thriving: {
      posture: 'upright',
      colorSaturation: 1.0,
      swayAmplitude: 2,
      droopAngle: 0,
      browning: 0,
      alpha: 1.0
    },
    lazy: {
      posture: 'droop',
      colorSaturation: 0.75,
      swayAmplitude: 4,
      droopAngle: 15,
      browning: 0.1,
      alpha: 0.95
    },
    withering: {
      posture: 'curl',
      colorSaturation: 0.5,
      swayAmplitude: 1,
      droopAngle: 30,
      browning: 0.3,
      alpha: 0.85
    },
    dormant: {
      posture: 'retract',
      colorSaturation: 0.3,
      swayAmplitude: 0.5,
      droopAngle: 45,
      browning: 0.7,
      alpha: 0.6
    }
  },
  ecology: {
    light: { optimal: 80, range: [40, 100] },
    moisture: { optimal: 45, range: [20, 70] },
    temperature: { optimal: 22, range: [10, 35] }
  }
};

export const Sunflower: PlantSpecies = {
  id: 'sunflower',
  name: '向日葵',
  description: '追逐阳光的金色花盘，饱满而温暖',
  speciesBaseValue: 15,
  bloomingIncomeRate: 0.035,
  stages: [
    {
      id: 'seed',
      name: '种子',
      duration: 360, // 6 game hours
      description: '饱满的黑白条纹瓜子沉睡在土中'
    },
    {
      id: 'sprout',
      name: '萌芽',
      duration: 600, // 10 game hours
      description: '两片厚实的子叶破土而出'
    },
    {
      id: 'seedling',
      name: '幼苗',
      duration: 900, // 15 game hours
      description: '粗糙的阔叶层层展开'
    },
    {
      id: 'budding',
      name: '结苞',
      duration: 1200, // 20 game hours
      description: '顶端鼓起绿色的花苞'
    },
    {
      id: 'blooming',
      name: '盛花',
      duration: Infinity,
      description: '金色花瓣围绕着深褐色花盘盛放'
    }
  ],
  states: {
    thriving: {
      posture: 'upright',
      colorSaturation: 1.0,
      swayAmplitude: 1.5,
      droopAngle: 0,
      browning: 0,
      alpha: 1.0
    },
    lazy: {
      posture: 'droop',
      colorSaturation: 0.8,
      swayAmplitude: 3,
      droopAngle: 20,
      browning: 0.1,
      alpha: 0.95
    },
    withering: {
      posture: 'curl',
      colorSaturation: 0.45,
      swayAmplitude: 0.8,
      droopAngle: 35,
      browning: 0.35,
      alpha: 0.8
    },
    dormant: {
      posture: 'retract',
      colorSaturation: 0.25,
      swayAmplitude: 0.3,
      droopAngle: 50,
      browning: 0.75,
      alpha: 0.55
    }
  },
  ecology: {
    light: { optimal: 90, range: [60, 100] },
    moisture: { optimal: 55, range: [30, 80] },
    temperature: { optimal: 25, range: [15, 35] }
  }
};

export const ALL_SPECIES: PlantSpecies[] = [Lavender, Sunflower];

export function getSpeciesById(id: string): PlantSpecies | undefined {
  return ALL_SPECIES.find(s => s.id === id);
}
